import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Logo } from '../components/Logo';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { CookieSettingsLink } from '../components/CookieBanner';
import { FileText, Building2, HardHat, Receipt, Check, Hammer, AlertTriangle, Clock, Wrench, Users, Quote, Bot, Sparkles, Mail, FileSignature, Target, TrendingUp, Camera, PhoneCall, Mic, Play, ArrowRight, ArrowLeft, ArrowDown, Coins, Zap, Rocket, Shield, Headphones, Brain } from 'lucide-react';
import { useAbVariant } from '../lib/useAbVariant';

const HERO_IMG = 'https://images.unsplash.com/photo-1685464196387-854858ce0f4f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85';

/**
 * Phone mockup that previews CantiereAI app (Preventivo AI generation screen).
 * Pure CSS / HTML — no external image, fully on-brand, scales beautifully.
 */
function HeroPhoneMockup({ t }) {
    return (
        <div className="relative mx-auto max-w-[340px] md:max-w-[380px]" data-testid="hero-phone-mockup" aria-hidden="true">
            {/* Soft glow behind the phone */}
            <div className="absolute inset-0 -z-10 blur-3xl opacity-40 bg-gradient-to-br from-[hsl(var(--accent))]/40 via-[hsl(45_100%_60%)]/20 to-transparent rounded-full" />

            {/* Phone frame */}
            <div className="relative bg-[hsl(220_15%_6%)] rounded-[2.8rem] p-[10px] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55),0_10px_30px_-10px_rgba(0,0,0,0.4)] border border-white/5">
                {/* Side accent (power button) */}
                <span className="absolute right-[-2px] top-28 w-[3px] h-12 bg-[hsl(220_15%_12%)] rounded-r" />
                <span className="absolute left-[-2px] top-24 w-[3px] h-8 bg-[hsl(220_15%_12%)] rounded-l" />
                <span className="absolute left-[-2px] top-40 w-[3px] h-12 bg-[hsl(220_15%_12%)] rounded-l" />

                {/* Screen */}
                <div className="relative rounded-[2.2rem] overflow-hidden bg-[hsl(220_13%_98%)] aspect-[9/19]">
                    {/* Dynamic island */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 w-24 h-6 bg-black rounded-full" />

                    {/* Status bar */}
                    <div className="relative z-10 flex items-center justify-between px-6 pt-3 pb-1 text-[10px] font-semibold text-[hsl(220_13%_10%)]">
                        <span>9:41</span>
                        <span className="flex items-center gap-1">
                            <span className="inline-block w-3.5 h-1.5 rounded-sm border border-[hsl(220_13%_10%)] relative"><span className="absolute inset-0.5 bg-[hsl(220_13%_10%)] rounded-[1px]" /></span>
                        </span>
                    </div>

                    {/* App content */}
                    <div className="relative px-4 pt-8 pb-3 flex flex-col h-full bg-gradient-to-b from-white to-[hsl(45_50%_97%)]">
                        {/* Top bar */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1.5">
                                <span className="w-7 h-7 rounded-md bg-[hsl(220_13%_10%)] text-[hsl(var(--accent))] flex items-center justify-center font-display text-sm">C</span>
                                <span className="font-display tracking-tight text-[hsl(220_13%_10%)]">CANTIEREAI</span>
                            </div>
                            <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
                        </div>

                        {/* Section label */}
                        <div className="text-[9px] font-bold tracking-widest text-[hsl(var(--muted-foreground))] mb-1">
                            {t('hero.phone_section', { defaultValue: 'PREVENTIVISTA AI' })}
                        </div>
                        <div className="font-display text-[20px] leading-tight text-[hsl(220_13%_10%)] mb-3">
                            {t('hero.phone_title', { defaultValue: 'Nuovo preventivo' })}
                        </div>

                        {/* Voice card */}
                        <div className="rounded-xl bg-[hsl(220_13%_10%)] text-white p-3 mb-3 relative overflow-hidden">
                            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-[hsl(var(--accent))]/20 blur-2xl" />
                            <div className="flex items-center gap-2 mb-2 relative z-10">
                                <div className="w-8 h-8 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] flex items-center justify-center shadow-lg">
                                    <Mic className="w-4 h-4" />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10px] font-bold tracking-wider text-[hsl(var(--accent))]">REC</div>
                                    <div className="text-[10px] text-white/70">00:24</div>
                                </div>
                                <div className="flex items-end gap-[2px] h-5">
                                    {[6, 12, 18, 10, 16, 8, 14, 11, 6].map((h, i) => (
                                        <span key={i} className="w-[2px] rounded-sm bg-[hsl(var(--accent))]" style={{ height: `${h}px` }} />
                                    ))}
                                </div>
                            </div>
                            <div className="relative z-10 text-[11px] leading-snug text-white/85 italic">
                                "{t('hero.phone_voice', { defaultValue: 'Ristrutturazione bagno 6 metri quadri con sanitari, piastrelle e impianto idraulico.' })}"
                            </div>
                        </div>

                        {/* Generated quote preview — detailed pro layout */}
                        <div className="rounded-xl bg-white border border-[hsl(var(--border))] p-3 mb-2 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[9px] font-bold tracking-wider text-[hsl(145_55%_34%)] flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" />
                                    {t('hero.phone_generated', { defaultValue: 'GENERATO IN 28s' })}
                                </div>
                                <div className="text-[8px] text-[hsl(var(--muted-foreground))] font-mono">PR-2026/047</div>
                            </div>

                            {/* Sezione 1: Opere edili */}
                            <div className="text-[7.5px] font-bold tracking-wider text-[hsl(var(--muted-foreground))] mb-1 mt-1">
                                {t('hero.phone_sec_edili', { defaultValue: 'OPERE EDILI' })}
                            </div>
                            <div className="space-y-[3px]">
                                {[
                                    { l: t('hero.phone_line_demolizioni', { defaultValue: 'Demolizioni + smaltimento rifiuti' }), v: '€ 500' },
                                    { l: t('hero.phone_line_controsoff', { defaultValue: 'Controsoffitto + faretti LED' }), v: '€ 700' },
                                    { l: t('hero.phone_line_piastrelle', { defaultValue: 'Piastrelle gres 6mq + finiture' }), v: '€ 810' },
                                ].map((row, i) => (
                                    <div key={i} className="flex items-center justify-between text-[10px] leading-tight">
                                        <span className="text-[hsl(220_13%_30%)] truncate pr-2">{row.l}</span>
                                        <span className="font-mono tabular-nums text-[hsl(220_13%_10%)] flex-shrink-0">{row.v}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Sezione 2: Impianti */}
                            <div className="text-[7.5px] font-bold tracking-wider text-[hsl(var(--muted-foreground))] mb-1 mt-2">
                                {t('hero.phone_sec_impianti', { defaultValue: 'IMPIANTI' })}
                            </div>
                            <div className="space-y-[3px]">
                                {[
                                    { l: t('hero.phone_line_idraulico', { defaultValue: 'Impianto idraulico (tubaz. + 8h)' }), v: '€ 720' },
                                    { l: t('hero.phone_line_elettrico', { defaultValue: 'Impianto elettrico (12 punti + 6h)' }), v: '€ 1.020' },
                                ].map((row, i) => (
                                    <div key={i} className="flex items-center justify-between text-[10px] leading-tight">
                                        <span className="text-[hsl(220_13%_30%)] truncate pr-2">{row.l}</span>
                                        <span className="font-mono tabular-nums text-[hsl(220_13%_10%)] flex-shrink-0">{row.v}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Sezione 3: Sanitari e doccia */}
                            <div className="text-[7.5px] font-bold tracking-wider text-[hsl(var(--muted-foreground))] mb-1 mt-2">
                                {t('hero.phone_sec_sanitari', { defaultValue: 'SANITARI E DOCCIA' })}
                            </div>
                            <div className="space-y-[3px]">
                                {[
                                    { l: t('hero.phone_line_sanitari', { defaultValue: 'Sanitari completi + miscelatori' }), v: '€ 1.250' },
                                    { l: t('hero.phone_line_doccia', { defaultValue: 'Doccia walk-in (piatto + cristallo)' }), v: '€ 980' },
                                    { l: t('hero.phone_line_lav_spec', { defaultValue: 'Lavandino sospeso + specchio LED' }), v: '€ 570' },
                                ].map((row, i) => (
                                    <div key={i} className="flex items-center justify-between text-[10px] leading-tight">
                                        <span className="text-[hsl(220_13%_30%)] truncate pr-2">{row.l}</span>
                                        <span className="font-mono tabular-nums text-[hsl(220_13%_10%)] flex-shrink-0">{row.v}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Totals */}
                            <div className="mt-2 pt-2 border-t border-dashed border-[hsl(var(--border))] space-y-[2px]">
                                <div className="flex items-center justify-between text-[9.5px] text-[hsl(220_13%_35%)]">
                                    <span>{t('hero.phone_subtot', { defaultValue: 'Subtotale' })}</span>
                                    <span className="font-mono tabular-nums">€ 6.550</span>
                                </div>
                                <div className="flex items-center justify-between text-[9.5px] text-[hsl(220_13%_35%)]">
                                    <span>{t('hero.phone_iva', { defaultValue: 'IVA 10%' })}</span>
                                    <span className="font-mono tabular-nums">€ 655</span>
                                </div>
                                <div className="flex items-center justify-between pt-1 mt-1 border-t border-[hsl(220_13%_10%)]">
                                    <span className="text-[10px] font-bold tracking-wider text-[hsl(220_13%_10%)]">{t('hero.phone_total', { defaultValue: 'TOTALE' })}</span>
                                    <span className="font-display text-[17px] leading-none tabular-nums text-[hsl(220_13%_10%)]">€ 7.205</span>
                                </div>
                            </div>

                            {/* Footer pills (terms) — only "validità" on quotes; payment terms are an invoice concept. */}
                            <div className="mt-2 pt-2 border-t border-[hsl(var(--border))] flex items-center gap-1.5 text-[8px]">
                                <span className="px-1.5 py-0.5 rounded bg-[hsl(220_13%_10%)]/8 text-[hsl(220_13%_30%)] font-bold tracking-wide">
                                    {t('hero.phone_term_val', { defaultValue: 'VALIDO 30GG' })}
                                </span>
                            </div>
                        </div>

                        {/* Bottom action — close to the terms row above, with the iPhone home-indicator bar below */}
                        <div className="mt-3">
                            <div className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] rounded-xl py-2.5 text-center text-[12px] font-bold tracking-tight shadow-md flex items-center justify-center gap-1.5">
                                <Mail className="w-3.5 h-3.5" />
                                {t('hero.phone_send', { defaultValue: 'Invia al cliente' })}
                            </div>
                            <div className="h-1 w-24 mx-auto mt-3 mb-2 bg-[hsl(220_13%_10%)] rounded-full" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Landing() {
    const [yearly, setYearly] = useState(false);
    const { t } = useTranslation();

    // A/B test on hero primary CTA copy
    const { variant: ctaVariant, trackConversion: trackCtaClick } = useAbVariant('hero_cta_lang_test');
    const heroCtaLabel = ctaVariant === 'variant_a'
        ? t('hero.cta_variant_a', { defaultValue: t('hero.cta_primary') })
        : t('hero.cta_primary');

    return (
        <div className="min-h-screen bg-[hsl(var(--background))]">
            {/* NAV */}
            <nav className="sticky top-0 z-40 bg-[hsl(var(--background))]/95 backdrop-blur border-b border-[hsl(var(--border))]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Logo />
                    <div className="hidden md:flex items-center gap-6 text-sm font-medium">
                        <a href="#moduli" className="hover:text-[hsl(var(--accent))] transition-colors">{t('nav.modules')}</a>
                        <a href="#agenti-ai" className="hover:text-[hsl(var(--accent))] transition-colors flex items-center gap-1">{t('nav.ai_agents')} <span className="text-[9px] font-bold tracking-wider px-1 py-0.5 rounded bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]">{t('nav.plus_badge')}</span></a>
                        <a href="#forza-vendita" className="hover:text-[hsl(var(--accent))] transition-colors flex items-center gap-1">{t('nav.sales_force')} <span className="text-[9px] font-bold tracking-wider px-1 py-0.5 rounded bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]">{t('nav.plus_badge')}</span></a>
                        <a href="#prezzi" className="hover:text-[hsl(var(--accent))] transition-colors">{t('nav.pricing')}</a>
                        <a href="#faq" className="hover:text-[hsl(var(--accent))] transition-colors">{t('nav.faq')}</a>
                        <Link to="/login" className="text-xs font-normal text-[hsl(220_13%_40%)] hover:text-[hsl(var(--accent))] transition-colors" data-testid="nav-login-link">{t('common.login')}</Link>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <LanguageSwitcher />
                        {/* Login link visible on mobile too (text only, less prominent than CTA) */}
                        <Link
                            to="/login"
                            className="md:hidden text-sm font-medium text-[hsl(220_13%_10%)] hover:text-[hsl(var(--accent))] px-1.5 py-2 min-h-[44px] inline-flex items-center whitespace-nowrap"
                            data-testid="mobile-nav-login-link"
                        >
                            {t('common.login')}
                        </Link>
                        <Link to="/checkout" className="flex-shrink-0">
                            <Button
                                className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-semibold whitespace-nowrap px-3 sm:px-4 text-xs sm:text-sm"
                                data-testid="nav-cta-button"
                            >
                                <span className="sm:hidden">{t('hero.cta_short', { defaultValue: t('hero.cta_primary') })}</span>
                                <span className="hidden sm:inline">{t('hero.cta_primary')}</span>
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* HERO */}
            <section className="relative overflow-hidden bg-[hsl(220_13%_8%)] text-white">
                <div className="absolute inset-0 noise opacity-30" />
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative">
                    <div className="grid lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <Badge className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(var(--accent))] font-semibold mb-5 text-xs tracking-widest inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full">
                                <Zap className="w-3.5 h-3.5 fill-current" />
                                {t('hero.badge')}
                            </Badge>
                            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-none mb-5" data-testid="hero-headline">
                                {t('hero.title_1')}<br />
                                <span className="text-[hsl(var(--accent))]">{t('hero.title_2')}</span>
                            </h1>
                            <p className="text-lg text-white/80 max-w-xl mb-7 leading-7">
                                {t('hero.subtitle')}
                            </p>
                            <div className="flex flex-wrap gap-3 mb-8">
                                <Link to="/checkout" onClick={() => trackCtaClick('cta_click')}>
                                    <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-12 px-6 text-base gap-2" data-testid="hero-primary-cta">
                                        {heroCtaLabel}
                                        <ArrowRight className="w-4 h-4" />
                                    </Button>
                                </Link>
                                <a
                                    href="#risultati"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const el = document.getElementById('risultati');
                                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }}
                                    data-testid="hero-secondary-cta-link"
                                >
                                    <Button variant="outline" size="lg" className="bg-transparent border-white/20 text-white hover:bg-white/5 h-12 px-6 text-base gap-2" data-testid="hero-secondary-cta">
                                        {t('hero.cta_secondary')}
                                        <ArrowDown className="w-4 h-4" />
                                    </Button>
                                </a>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                {[
                                    t('hero.feature_1'),
                                    t('hero.feature_2'),
                                    t('hero.feature_3'),
                                    t('hero.feature_4'),
                                ].map((label, i) => (
                                    <div key={i} className="flex items-center gap-2 text-white/90">
                                        <Check className="w-4 h-4 text-[hsl(var(--accent))]" />
                                        <span>{label}</span>
                                    </div>
                                ))}
                            </div>

                            {/* DVR Banner */}
                            <a
                                href="#dvr"
                                className="block mt-6 rounded-xl p-5 border border-[hsl(45_100%_50%)] bg-gradient-to-br from-[hsl(45_100%_50%)] to-[hsl(48_100%_55%)] text-[hsl(220_13%_8%)] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 group"
                                data-testid="hero-dvr-banner"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[hsl(220_13%_8%)] text-[hsl(45_100%_55%)] flex items-center justify-center shadow-md">
                                        <Shield className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display text-lg sm:text-xl leading-tight tracking-tight">
                                            {t('hero.dvr_title', { defaultValue: 'DVR CONFORMI D.LGS 81/2008' })}
                                        </div>
                                        <p className="text-sm text-[hsl(220_13%_22%)] mt-1 leading-snug">
                                            {t('hero.dvr_subtitle', { defaultValue: 'Il primo servizio DVR italiano AI + Professionista. Pronti in 48 ore. Stop alle multe.' })}
                                        </p>
                                        <div className="mt-3 flex justify-end">
                                            <span className="text-sm font-bold text-[hsl(220_13%_8%)] inline-flex items-center gap-1 group-hover:underline underline-offset-4 decoration-2">
                                                {t('hero.dvr_cta', { defaultValue: 'Scopri il servizio' })}
                                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </div>
                        <div className="relative flex items-center justify-center">
                            <HeroPhoneMockup t={t} />
                        </div>
                    </div>
                </div>
            </section>

            {/* PROBLEM */}
            <section className="py-16 lg:py-20 bg-[hsl(0_0%_98%)]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="font-display text-xs tracking-widest text-[hsl(var(--muted-foreground))] mb-2">{t('problem.label')}</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-tight whitespace-pre-line">{t('problem.title')}</h2>
                        <p className="mt-4 text-base md:text-lg text-[hsl(220_13%_35%)] max-w-2xl mx-auto leading-relaxed">
                            {t('problem.subtitle')}
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            { icon: Clock, t: t('problem.p1_t'), d: t('problem.p1_d') },
                            { icon: Headphones, t: t('problem.p2_t'), d: t('problem.p2_d') },
                            { icon: Receipt, t: t('problem.p3_t'), d: t('problem.p3_d') },
                            { icon: Brain, t: t('problem.p4_t'), d: t('problem.p4_d') },
                        ].map((p, i) => (
                            <Card key={i} className="p-6 border-[hsl(var(--border))]">
                                <p.icon className="w-8 h-8 text-[hsl(var(--accent))] mb-3" />
                                <h3 className="font-display text-xl mb-1">{p.t}</h3>
                                <p className="text-sm text-[hsl(var(--muted-foreground))]">{p.d}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* MODULES → PLATFORM */}
            <section id="moduli" className="py-16 lg:py-20 bg-[hsl(220_13%_8%)] text-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="font-display text-xs tracking-widest text-[hsl(var(--accent))] mb-2">{t('modules.label')}</div>
                        <h2 className="font-display text-4xl md:text-5xl">{t('modules.title')}</h2>
                        <p className="mt-4 text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
                            {t('modules.subtitle')}
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            { key: 'crm', icon: Users, t: t('modules.crm_t'), d: t('modules.crm_d') },
                            { key: 'preventivi', icon: FileText, t: t('modules.preventivi_t'), d: t('modules.preventivi_d') },
                            { key: 'sal', icon: Building2, t: t('modules.sal_t'), d: t('modules.sal_d') },
                            { key: 'fatture', icon: Receipt, t: t('modules.fatture_t'), d: t('modules.fatture_d') },
                            { key: 'sicurezza', icon: HardHat, t: t('modules.sicurezza_t'), d: t('modules.sicurezza_d') },
                            { key: 'agenti', icon: Bot, t: t('modules.agenti_teaser_t'), d: t('modules.agenti_teaser_d'), cta: t('modules.agenti_teaser_cta'), highlight: true },
                        ].map((m) => {
                            const isSicurezza = m.key === 'sicurezza';
                            const isHighlight = m.highlight;
                            return (
                                <div
                                    key={m.key}
                                    id={isSicurezza ? 'dvr' : undefined}
                                    className={`p-6 bg-[hsl(220_13%_10%)] border rounded-lg transition-colors scroll-mt-24 flex flex-col ${isHighlight ? 'border-[hsl(var(--accent))] shadow-[0_0_0_1px_hsl(var(--accent))/0.4]' : isSicurezza ? 'border-[hsl(var(--accent))]/50' : 'border-[hsl(220_10%_22%)] hover:border-[hsl(var(--accent))]'}`}
                                >
                                    <div className="w-12 h-12 bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30 rounded flex items-center justify-center mb-4">
                                        <m.icon className="w-6 h-6 text-[hsl(var(--accent))]" />
                                    </div>
                                    <h3 className="font-display text-2xl mb-2 tracking-wide">{m.t}</h3>
                                    <p className="text-sm text-white/70 leading-6 flex-1">{m.d}</p>
                                    {m.cta && (
                                        <a
                                            href="#agenti-ai"
                                            className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[hsl(var(--accent))] hover:text-white transition-colors group/cta"
                                            data-testid="modules-agenti-cta"
                                        >
                                            {m.cta}
                                            <ArrowRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-0.5" />
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="prezzi" className="py-16 lg:py-20 bg-[hsl(0_0%_98%)]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <div className="font-display text-xs tracking-widest text-[hsl(var(--muted-foreground))] mb-2">{t('pricing.label')}</div>
                        <h2 className="font-display text-4xl md:text-5xl mb-4">{t('pricing.title')}</h2>
                        <div className="inline-flex items-center gap-3 bg-white border border-[hsl(var(--border))] rounded-full p-1">
                            <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${!yearly ? 'bg-[hsl(220_13%_10%)] text-white' : 'text-[hsl(var(--muted-foreground))]'}`} data-testid="pricing-toggle-monthly">{t('pricing.monthly')}</button>
                            <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${yearly ? 'bg-[hsl(220_13%_10%)] text-white' : 'text-[hsl(var(--muted-foreground))]'}`} data-testid="pricing-toggle-yearly">
                                {t('pricing.yearly')} {yearly && <span className="ml-1 text-[hsl(var(--accent))]">{t('pricing.yearly_save')}</span>}
                            </button>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { id: 'artigiano', name: t('pricing.art_name'), price_m: 99, price_y: 1069, features: t('pricing.art_f', { returnObjects: true }) },
                            { id: 'pmi', name: t('pricing.pmi_name'), price_m: 149, price_y: 1609, recommended: true, features: t('pricing.pmi_f', { returnObjects: true }) },
                            { id: 'impresa', name: t('pricing.imp_name'), price_m: 299, price_y: 3229, features: t('pricing.imp_f', { returnObjects: true }) },
                        ].map((p) => (
                            <Card key={p.id} className={`p-7 relative ${p.recommended ? 'border-[hsl(var(--accent))] border-2 shadow-xl' : 'border-[hsl(var(--border))]'}`} data-testid={`pricing-card-${p.id}`}>
                                {p.recommended && (
                                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(var(--accent))] font-bold">
                                        {t('pricing.recommended')}
                                    </Badge>
                                )}
                                <h3 className="font-display text-2xl tracking-wide mb-1">{p.name}</h3>
                                <div className="flex items-end gap-1 mb-1">
                                    <span className="font-display text-5xl">€{yearly ? Math.round(p.price_y / 12) : p.price_m}</span>
                                    <span className="text-sm text-[hsl(var(--muted-foreground))] mb-2">{t('pricing.per_month')}</span>
                                </div>
                                {yearly && <div className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{t('pricing.billed_yearly', { amount: p.price_y })}</div>}
                                {!yearly && <div className="h-4 mb-4" />}
                                <ul className="space-y-2 mb-6">
                                    {(Array.isArray(p.features) ? p.features : []).map((f, i) => {
                                        const isInheritedPlanRef = i === 0 && (p.id === 'pmi' || p.id === 'impresa');
                                        return (
                                            <li key={i} className="flex items-start gap-2 text-sm">
                                                {isInheritedPlanRef ? (
                                                    <ArrowLeft className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" data-testid={`pricing-${p.id}-inherit-arrow`} />
                                                ) : (
                                                    <Check className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                                                )}
                                                <span className={isInheritedPlanRef ? 'font-semibold' : ''}>{f}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <Link to={`/checkout?plan=${p.id}&period=${yearly ? 'yearly' : 'monthly'}`}>
                                    <Button className={`w-full font-semibold ${p.recommended ? 'bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)]' : 'bg-[hsl(220_13%_10%)] text-white hover:bg-[hsl(220_13%_20%)]'}`} data-testid={`pricing-cta-${p.id}`}>
                                        {t('pricing.choose', { plan: p.name })}
                                    </Button>
                                </Link>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING PLUS+ — FORZA VENDITA */}
            <section id="forza-vendita" className="py-16 lg:py-20 bg-gradient-to-br from-[hsl(220_13%_8%)] via-[hsl(220_13%_10%)] to-[hsl(220_13%_12%)] text-white relative overflow-hidden">
                <div className="absolute inset-0 noise opacity-20" />
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="text-center mb-10">
                        <Badge className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(var(--accent))] font-bold mb-4 text-xs tracking-widest">
                            {t('forza_vendita_sec.badge')}
                        </Badge>
                        <h2 className="font-display text-4xl md:text-6xl mb-3 flex items-center justify-center gap-3 flex-wrap">
                            <Bot className="w-12 h-12 md:w-14 md:h-14 text-[hsl(var(--accent))]" />
                            {t('forza_vendita_sec.title')}
                        </h2>
                        <p className="text-white/75 text-base md:text-lg max-w-2xl mx-auto">
                            {t('forza_vendita_sec.subtitle_p1')} <span className="text-[hsl(var(--accent))] font-semibold">{t('forza_vendita_sec.subtitle_em')}</span> {t('forza_vendita_sec.subtitle_p2')}
                        </p>
                    </div>

                    {/* Features highlight */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 max-w-5xl mx-auto">
                        {[
                            { icon: Target, t: t('forza_vendita_sec.f1_t'), d: t('forza_vendita_sec.f1_d') },
                            { icon: Mail, t: t('forza_vendita_sec.f2_t'), d: t('forza_vendita_sec.f2_d') },
                            { icon: FileSignature, t: t('forza_vendita_sec.f3_t'), d: t('forza_vendita_sec.f3_d') },
                            { icon: TrendingUp, t: t('forza_vendita_sec.f4_t'), d: t('forza_vendita_sec.f4_d') },
                        ].map((f, i) => (
                            <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-sm hover:border-[hsl(var(--accent))]/50 transition-colors">
                                <f.icon className="w-6 h-6 text-[hsl(var(--accent))] mb-2" />
                                <div className="font-semibold text-sm mb-1">{f.t}</div>
                                <div className="text-xs text-white/60">{f.d}</div>
                            </div>
                        ))}
                    </div>

                    {/* Included-in-IMPRESA-AI banner (replaces the old pricing cards) */}
                    <div className="max-w-3xl mx-auto">
                        <div className="rounded-2xl p-6 md:p-8 bg-gradient-to-br from-[hsl(45_100%_50%)] to-[hsl(48_100%_55%)] text-[hsl(220_13%_8%)] shadow-2xl border-2 border-[hsl(var(--accent))]/40 flex flex-col md:flex-row items-center md:items-stretch gap-5">
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[hsl(220_13%_8%)] text-[hsl(var(--accent))] flex items-center justify-center shadow-lg">
                                <Sparkles className="w-7 h-7" />
                            </div>
                            <div className="flex-1 text-center md:text-left">
                                <div className="text-[10px] font-bold tracking-widest uppercase opacity-70 mb-1">
                                    {t('forza_vendita_sec.included_eyebrow', { defaultValue: 'Incluso · Nessun costo aggiuntivo' })}
                                </div>
                                <div className="font-display text-xl md:text-2xl leading-tight mb-2">
                                    {t('forza_vendita_sec.included_title', { defaultValue: 'Tutti gli agenti AI sono inclusi nel piano IMPRESA AI €299/mese.' })}
                                </div>
                                <div className="text-sm opacity-80">
                                    {t('forza_vendita_sec.included_subtitle', { defaultValue: 'Niente add-on, niente sorprese in fattura. Un unico piano, tutto attivo.' })}
                                </div>
                            </div>
                            <div className="flex items-center flex-shrink-0">
                                <a href="#prezzi">
                                    <Button className="bg-[hsl(220_13%_8%)] text-white hover:bg-[hsl(220_13%_18%)] font-bold h-11 px-5 gap-2" data-testid="forza-vendita-included-cta">
                                        {t('forza_vendita_sec.included_cta', { defaultValue: 'Scopri il piano Impresa AI' })}
                                        <ArrowRight className="w-4 h-4" />
                                    </Button>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="text-center mt-8 text-sm text-white/60">
                        {t('forza_vendita_sec.requires_note')}
                    </div>
                </div>
            </section>

            {/* PRICING PLUS+ — AGENTI AI */}
            <section id="agenti-ai" className="py-16 lg:py-20 bg-[hsl(0_0%_96%)]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <Badge className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(var(--accent))] font-bold mb-4 text-xs tracking-widest">
                            {t('agenti_sec.badge')}
                        </Badge>
                        <h2 className="font-display text-4xl md:text-6xl mb-3 flex items-center justify-center gap-3 flex-wrap">
                            <Sparkles className="w-12 h-12 md:w-14 md:h-14 text-[hsl(var(--accent))]" />
                            {t('agenti_sec.title')}
                        </h2>
                        <p className="text-[hsl(var(--muted-foreground))] text-base md:text-lg max-w-2xl mx-auto">
                            <strong>{t('agenti_sec.subtitle_strong')}</strong> {t('agenti_sec.subtitle_rest')}
                        </p>
                    </div>

                    {/* 7 agenti cards */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                        {[
                            { icon: Users, t: t('agenti_sec.a1_t'), d: t('agenti_sec.a1_d'), tag: t('agenti_sec.a1_tag') },
                            { icon: Target, t: t('agenti_sec.a2_t'), d: t('agenti_sec.a2_d'), tag: t('agenti_sec.a2_tag') },
                            { icon: Mail, t: t('agenti_sec.a3_t'), d: t('agenti_sec.a3_d'), tag: t('agenti_sec.a3_tag') },
                            { icon: Sparkles, t: t('agenti_sec.a4_t'), d: t('agenti_sec.a4_d'), tag: t('agenti_sec.a4_tag') },
                            { icon: PhoneCall, t: t('agenti_sec.a5_t'), d: t('agenti_sec.a5_d'), tag: t('agenti_sec.a5_tag') },
                            { icon: FileSignature, t: t('agenti_sec.a6_t'), d: t('agenti_sec.a6_d'), tag: t('agenti_sec.a6_tag') },
                            { icon: Receipt, t: t('agenti_sec.a7_t'), d: t('agenti_sec.a7_d'), tag: t('agenti_sec.a7_tag') },
                        ].map((a, i) => (
                            <Card key={i} className={`p-5 border-[hsl(var(--border))] hover:border-[hsl(var(--accent))] transition-colors ${i === 6 ? 'lg:col-start-2' : ''}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="w-10 h-10 bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30 rounded flex items-center justify-center">
                                        <a.icon className="w-5 h-5 text-[hsl(var(--accent))]" />
                                    </div>
                                    <Badge className="bg-[hsl(220_13%_10%)] text-white hover:bg-[hsl(220_13%_10%)] text-[10px]">{a.tag}</Badge>
                                </div>
                                <h3 className="font-display text-lg mb-1 tracking-wide">{a.t}</h3>
                                <p className="text-xs text-[hsl(var(--muted-foreground))]">{a.d}</p>
                            </Card>
                        ))}
                    </div>

                    {/* Included-in-IMPRESA-AI banner (replaces the old single pricing card) */}
                    <div className="max-w-3xl mx-auto">
                        <div className="rounded-2xl p-6 md:p-8 bg-[hsl(220_13%_10%)] text-white shadow-2xl border-2 border-[hsl(var(--accent))] flex flex-col md:flex-row items-center md:items-stretch gap-5">
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] flex items-center justify-center shadow-lg">
                                <Sparkles className="w-7 h-7" />
                            </div>
                            <div className="flex-1 text-center md:text-left">
                                <div className="text-[10px] font-bold tracking-widest uppercase text-[hsl(var(--accent))] mb-1">
                                    {t('agenti_sec.included_eyebrow', { defaultValue: 'Incluso · Nessun costo aggiuntivo' })}
                                </div>
                                <div className="font-display text-xl md:text-2xl leading-tight mb-2">
                                    {t('agenti_sec.included_title', { defaultValue: 'Tutti gli agenti AI sono inclusi nel piano IMPRESA AI €299/mese.' })}
                                </div>
                                <div className="text-sm text-white/70">
                                    {t('agenti_sec.included_subtitle', { defaultValue: 'Niente add-on, niente sorprese in fattura. Un unico piano, tutto attivo.' })}
                                </div>
                            </div>
                            <div className="flex items-center flex-shrink-0">
                                <a href="#prezzi">
                                    <Button className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(45_100%_48%)] font-bold h-11 px-5 gap-2" data-testid="agenti-included-cta">
                                        {t('agenti_sec.included_cta', { defaultValue: 'Scopri il piano Impresa AI' })}
                                        <ArrowRight className="w-4 h-4" />
                                    </Button>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* RESULTS / "I NUMERI" — Replaces fake testimonials. Stime di valore basate sui workflow del prodotto. */}
            <section id="risultati" className="py-16 lg:py-20 bg-white scroll-mt-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="font-display text-xs tracking-widest text-[hsl(var(--accent))] mb-2">{t('results.label')}</div>
                        <h2 className="font-display text-4xl md:text-5xl">{t('results.title')}</h2>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-3 max-w-xl mx-auto">{t('results.subtitle')}</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { icon: Clock, stat: t('results.s1_stat'), label: t('results.s1_label'), hint: t('results.s1_hint') },
                            { icon: TrendingUp, stat: t('results.s2_stat'), label: t('results.s2_label'), hint: t('results.s2_hint') },
                            { icon: Coins, stat: t('results.s3_stat'), label: t('results.s3_label'), hint: t('results.s3_hint') },
                        ].map((item, i) => (
                            <Card
                                key={i}
                                className="p-8 text-center hover:border-[hsl(var(--accent))]/60 transition-colors"
                                data-testid={`results-card-${i}`}
                            >
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30 mb-4">
                                    <item.icon className="w-6 h-6 text-[hsl(var(--accent))]" />
                                </div>
                                <div className="font-display text-6xl md:text-7xl leading-none text-[hsl(var(--accent))] mb-3">
                                    {item.stat}
                                </div>
                                <div className="font-semibold text-base mb-2">{item.label}</div>
                                <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{item.hint}</p>
                            </Card>
                        ))}
                    </div>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))] text-center mt-8 max-w-2xl mx-auto leading-relaxed" data-testid="results-disclaimer">
                        * {t('results.disclaimer')}
                    </p>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="py-16 lg:py-20 bg-[hsl(0_0%_98%)]">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <div className="font-display text-xs tracking-widest text-[hsl(var(--muted-foreground))] mb-2">{t('faq.label')}</div>
                        <h2 className="font-display text-4xl md:text-5xl">{t('faq.title')}</h2>
                    </div>
                    <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
                        {[
                            { q: t('faq.q1'), a: t('faq.a1') },
                            { q: t('faq.q2'), a: t('faq.a2') },
                            { q: t('faq.q3'), a: t('faq.a3') },
                            { q: t('faq.q4'), a: t('faq.a4') },
                            { q: t('faq.q5'), a: t('faq.a5') },
                        ].map((f, i) => (
                            <AccordionItem key={i} value={`item-${i}`} className="border border-[hsl(var(--border))] rounded-lg px-5 bg-white">
                                <AccordionTrigger className="text-left font-semibold hover:no-underline py-4" data-testid={`faq-item-${i}`}>{f.q}</AccordionTrigger>
                                <AccordionContent className="text-[hsl(var(--muted-foreground))] pb-4">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 lg:py-20 bg-[hsl(220_13%_8%)] text-white relative overflow-hidden">
                <div className="absolute inset-0 noise opacity-30" />
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
                    <h2 className="font-display text-5xl md:text-6xl mb-4">{t('cta_bottom.title')}</h2>
                    <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">{t('cta_bottom.subtitle')}</p>
                    <Link to="/checkout">
                        <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-14 px-8 text-lg" data-testid="cta-bottom-button">
                            {t('cta_bottom.button')}
                        </Button>
                    </Link>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-[hsl(220_13%_8%)] text-white/70 text-sm border-t border-white/5">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-8">
                        {/* Left: Logo + tagline */}
                        <div className="md:max-w-sm">
                            <Logo variant="light" />
                            <p className="mt-3 text-white/55 leading-relaxed">{t('footer.tagline')}</p>
                        </div>

                        {/* Right: legal + contact + nav */}
                        <div className="text-xs md:text-right space-y-3">
                            {/* Company line */}
                            <div className="flex items-center md:justify-end gap-2 text-white/75">
                                <span className="font-display tracking-wider text-[hsl(var(--accent))] text-sm">SynoraOs</span>
                                <span className="text-white/25">·</span>
                                <span className="font-mono tracking-wide">P.IVA 18308681008</span>
                            </div>
                            {/* Email */}
                            <div className="flex items-center md:justify-end">
                                <a
                                    href="mailto:info@cantiereai.com"
                                    className="inline-flex items-center gap-1.5 text-white/70 hover:text-[hsl(var(--accent))] transition-colors group"
                                    data-testid="footer-email"
                                >
                                    <Mail className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
                                    <span className="underline-offset-4 group-hover:underline">info@cantiereai.com</span>
                                </a>
                            </div>
                            {/* Nav pills */}
                            <div className="flex gap-x-4 gap-y-2 md:justify-end mt-4 flex-wrap text-white/60">
                                <Link to="/install" className="hover:text-white transition-colors" data-testid="footer-install">{t('footer.install_app')}</Link>
                                <span className="text-white/15">·</span>
                                <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">{t('footer.privacy')}</Link>
                                <span className="text-white/15">·</span>
                                <Link to="/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies">{t('footer.cookies')}</Link>
                                <span className="text-white/15">·</span>
                                <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">{t('footer.terms')}</Link>
                                <span className="text-white/15">·</span>
                                <CookieSettingsLink className="hover:text-white transition-colors cursor-pointer" />
                            </div>
                        </div>
                    </div>

                    {/* Copyright bar */}
                    <div className="mt-8 pt-5 border-t border-white/5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-[11px] text-white/40">
                        <div className="flex items-center gap-1.5">
                            <span>© {new Date().getFullYear()}</span>
                            <span className="font-display tracking-wider text-white/60">SynoraOs</span>
                            <span>·</span>
                            <span>{t('footer.all_rights', { defaultValue: 'Tutti i diritti riservati.' })}</span>
                        </div>
                        <div className="tracking-wider uppercase text-[10px] text-white/30">
                            CantiereAI · Made in Italy 🇮🇹
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
